import { Field } from '@base-ui/react/field'
import styled, { css } from 'styled-components'
import type { FormLayout } from 'sutra/components/Form/form-layout'
import { accents, semantic } from 'sutra/tokens/colors'
import { typography } from 'sutra/tokens/typography'

export type FormItemStatus = 'error' | 'warning' | 'success'

const STATUS_COLOR: Record<FormItemStatus, string> = {
  error: accents.crimsonBloom[600],
  warning: accents.goldenLight[700],
  success: accents.verdantGreen[700],
}

export const StyledForm = styled.form<{ $layout: FormLayout }>`
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.sm.fontSize}px;
  line-height: ${typography.text.sm.lineHeight}px;
  color: ${semantic.fg};

  ${({ $layout }) =>
    $layout === 'inline' &&
    css`
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
    `}
`

const itemLayout = css<{ $layout: FormLayout }>`
  display: flex;
  margin-bottom: 24px;

  ${({ $layout }) =>
    $layout === 'vertical'
      ? css`
          flex-direction: column;
          gap: 8px;
        `
      : css`
          flex-direction: row;
          align-items: flex-start;
        `}

  ${({ $layout }) =>
    $layout === 'inline' &&
    css`
      flex: none;
      margin-inline-end: 16px;
    `}
`

export const ItemRoot = styled(Field.Root)<{ $layout: FormLayout }>`
  ${itemLayout}
`

// For a control that is a set of controls: `Field.Root` assumes one control,
// so inside it every radio shares one id and one label.
export const GroupRoot = styled.div<{ $layout: FormLayout }>`
  ${itemLayout}
`

export const LabelCell = styled.div<{
  $layout: FormLayout
  $width: string | undefined
}>`
  box-sizing: border-box;
  ${({ $layout, $width }) =>
    $layout === 'vertical'
      ? css`
          width: 100%;
        `
      : css`
          display: flex;
          align-items: center;
          justify-content: ${$layout === 'horizontal'
            ? 'flex-end'
            : 'flex-start'};
          min-height: 40px;
          padding-inline-end: 12px;
          flex: ${$width ? `0 0 ${$width}` : '0 0 auto'};
          max-width: ${$width ?? 'none'};
        `}
`

// A plain <label>, not `Field.Label`: that registers itself as the accessible
// name of every control in the field, which would replace a radio's or
// checkbox's own text. The asterisk is a pseudo-element, so it is not part of
// the label's text and `getByLabelText('Name')` still matches a required field.
export const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: ${typography.weights.medium};
  color: ${semantic.fg};

  &[data-required]::before {
    content: '*';
    color: ${accents.crimsonBloom[600]};
  }
`

export const ControlCell = styled.div<{
  $layout: FormLayout
  $width: string | undefined
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  ${({ $layout, $width }) =>
    $layout === 'vertical'
      ? css`
          width: 100%;
        `
      : css`
          flex: ${$width ? `0 0 ${$width}` : '1 1 auto'};
          max-width: ${$width ?? 'none'};
        `}
`

export const FieldError = styled(Field.Error)<{
  $status: FormItemStatus | undefined
}>`
  font-size: ${typography.text.xs.fontSize}px;
  line-height: ${typography.text.xs.lineHeight}px;
  color: ${({ $status = 'error' }) => STATUS_COLOR[$status]};
`

export const FieldDescription = styled(Field.Description)`
  font-size: ${typography.text.xs.fontSize}px;
  line-height: ${typography.text.xs.lineHeight}px;
  color: ${semantic.fgMuted};
`
